import type { ReactNode, SyntheticEvent } from "react";

import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

import { AccessibleIcon } from "@/components/accessible-icon";
import { Button } from "@/components/button";

import type { Platform } from "../../schedule-wizard/post-type-step/postTypes";
import type { ScheduledPost } from "../scheduledPost";

import { DesignImage } from "../../media/DesignImage";
import { PostVideo } from "./PostVideo";
import { sampleAverageColor } from "./sampleAverageColor";

interface FeedLayout {
  frameMinRatio: number;
  imageMaxRatio: number;
  imageMinRatio: number;
}

const FEED_LAYOUTS: Record<Platform, FeedLayout> = {
  facebook: {
    frameMinRatio: 500 / 575,
    imageMaxRatio: 1.91,
    imageMinRatio: 2 / 3,
  },
  instagram: {
    frameMinRatio: 4 / 5,
    imageMaxRatio: 1.91,
    imageMinRatio: 4 / 5,
  },
};

const SLIDE_TRANSITION = { duration: 0.35, ease: [0.32, 0.72, 0, 1] } as const;

const slideVariants = {
  center: { x: "0%" },
  enter: (direction: number) => ({ x: `${direction * 100}%` }),
  exit: (direction: number) => ({ x: `${direction * -100}%` }),
};

export function PostImage({ post }: { post: ScheduledPost }) {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [imageRatio, setImageRatio] = useState(1);
  const [backdropColor, setBackdropColor] = useState<null | string>(null);
  const { images } = post;

  if (post.video) {
    return (
      <PostVideo
        className="aspect-square w-full object-cover"
        title={post.title}
        video={post.video}
      />
    );
  }

  if (images.length === 0) {
    return (
      <div className="flex aspect-square w-full items-center justify-center bg-bg-secondary">
        <ImageIcon className="size-10 text-fg-quaternary" />
      </div>
    );
  }

  const current = Math.min(index, images.length - 1);
  const isCarousel = images.length > 1;
  const layout = FEED_LAYOUTS[post.platform ?? "facebook"];

  function showImage(next: number) {
    setDirection(next > current ? 1 : -1);
    setIndex(next);
  }

  function measureFirstImage(event: SyntheticEvent<HTMLImageElement>) {
    if (current !== 0) return;
    const { currentSrc, naturalHeight, naturalWidth } = event.currentTarget;

    setImageRatio(
      Math.min(
        Math.max(naturalWidth / naturalHeight, layout.imageMinRatio),
        layout.imageMaxRatio,
      ),
    );
    void sampleAverageColor(currentSrc).then(setBackdropColor);
  }

  return (
    <div
      className="relative flex w-full justify-center bg-bg-secondary"
      style={{
        aspectRatio: Math.max(imageRatio, layout.frameMinRatio),
        backgroundColor: backdropColor ?? undefined,
      }}
    >
      <div
        className="relative h-full max-w-full overflow-hidden"
        style={{ aspectRatio: imageRatio }}
      >
        <MotionConfig reducedMotion="user">
          <AnimatePresence custom={direction} initial={false}>
            <motion.div
              animate="center"
              className="absolute inset-0"
              custom={direction}
              exit="exit"
              initial="enter"
              key={current}
              transition={SLIDE_TRANSITION}
              variants={slideVariants}
            >
              <DesignImage
                alt={
                  isCarousel
                    ? `Post image ${current + 1} of ${images.length} for ${post.title}`
                    : `Post image for ${post.title}`
                }
                className="size-full object-cover"
                onLoad={measureFirstImage}
                sources={images[current] ?? []}
              />
            </motion.div>
          </AnimatePresence>
        </MotionConfig>
      </div>
      {isCarousel && (
        <>
          <span className="absolute right-md top-md rounded-full bg-black/60 px-md py-xxs text-xs font-medium text-white">
            {current + 1}/{images.length}
          </span>
          {current > 0 && (
            <CarouselButton
              className="left-md"
              label="Previous image"
              onPress={() => showImage(current - 1)}
            >
              <ChevronLeft />
            </CarouselButton>
          )}
          {current < images.length - 1 && (
            <CarouselButton
              className="right-md"
              label="Next image"
              onPress={() => showImage(current + 1)}
            >
              <ChevronRight />
            </CarouselButton>
          )}
          <div className="absolute inset-x-0 bottom-md flex justify-center gap-xs">
            {images.map((sources, dot) => (
              <span
                className={twMerge(
                  "size-1.5 rounded-full bg-white/50 transition-colors duration-300",
                  dot === current && "bg-white",
                )}
                key={sources.join(",") + dot}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CarouselButton({
  children,
  className,
  label,
  onPress,
}: {
  children: ReactNode;
  className: string;
  label: string;
  onPress: () => void;
}) {
  return (
    <Button
      aria-label={label}
      className={twMerge(
        "absolute top-1/2 z-10 flex size-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 p-0 text-gray-900 shadow-sm [&_svg]:size-4",
        className,
      )}
      isIconOnly
      onPress={onPress}
      variant="unstyled"
    >
      <AccessibleIcon>{children}</AccessibleIcon>
    </Button>
  );
}
